import { iconButtonClasses, IconButton as MuiIconButton, styled } from '@mui/material';
import type { IconButtonProps as MuiIconButtonProps } from '@mui/material';

export interface IconButtonProps extends MuiIconButtonProps {
  destructive?: boolean;
  'data-testid'?: string;
}

/**
 * Icon Button component, part of Delogue design system.
 * @param onClick - on click handler
 * @param size - size of the button, can be lg, md, sm
 * @param children - button content, usually a phosphor icon
 * @param destructive - if true, the button will have a destructive/red style
 * @param disabled - if true, the button will be disabled
 */
export const IconButton = ({ children, 'data-testid': dataTestId, ...rest }: IconButtonProps) => {
  return (
    <StyledIconButton data-testid={dataTestId || 'icon-button'} {...rest}>
      {children}
    </StyledIconButton>
  );
};

const StyledIconButton = styled(({ destructive, ...props }: IconButtonProps) => (
  <MuiIconButton {...props} />
))(({ theme, destructive }) => {
  const colors = {
    color: destructive ? theme.palette.fg.errorPrimary : theme.palette.fg.brandPrimary,
    backgroundColor: destructive ? theme.palette.bg.errorPrimary : theme.palette.bg.brandPrimary,
  };
  return {
    borderRadius: '4px',
    color: theme.palette.fg.secondary,
    '&:hover': { ...colors },
    '&:focus': { ...colors },
    [`&.${iconButtonClasses.disabled}`]: {
      backgroundColor: theme.palette.utility.grey[50],
      color: theme.palette.text.disabledSubtle,
    },
  };
});

export const smIconButton = {
  width: '20px',
  height: '20px',
  padding: '2px',
  justifyContent: 'center',
  alignItems: 'center',
};

export const mdIconButton = {
  width: '24px',
  height: '24px',
  padding: '2px',
  justifyContent: 'center',
  alignItems: 'center',
};

export const lgIconButton = {
  width: '32px',
  height: '32px',
  padding: '4px',
  justifyContent: 'center',
  alignItems: 'center',
};
